/**
 * SettleNowDialog — issue #251 (go-live audit finding 5).
 *
 * "Check Out & Settle Now" previously took NO payment: it flipped the
 * booking to checked_out and stopped — no charge, no Payment row, no
 * OpenInvoice — silently exiting the money funnel (and inflating gross
 * revenue, which counts checked-out bookings). This dialog is the second
 * half of the fixed flow:
 *
 *   1. Booking is checked out (operational leg — dog leaves).
 *   2. An OpenInvoice row is created on the server-of-record (money leg —
 *      ALWAYS, both tracks; done by the Bookings orchestrator BEFORE this
 *      dialog opens).
 *   3. This dialog collects the payment:
 *        - "Card on file" → POST /api/v2/admin/payments/card-on-file/charge
 *          (#248 real money endpoint). The server creates the Payment row
 *          AND atomically settles the booking's open invoice(s) inside
 *          completeDeskPayment — no client settle call needed.
 *        - "Record as paid" (cash / external terminal) →
 *          PATCH /api/v2/admin/payments/open-invoices/:id/settle, which
 *          atomically settles + mints the real Payment ledger row (#248).
 *        - "Settle later" / close → the invoice REMAINS in the end-of-day
 *          queue (Payments → open invoices). Nothing exits silently: by
 *          the time this dialog opens the money record already exists.
 *
 * Idempotency (#251 rule): ONE key per logical attempt — minted by the
 * orchestrator when the dialog opens and held stable for its lifetime; a
 * staff retry after a failed charge re-sends the SAME key. Money POSTs are
 * never transport-retried (adminPaymentsApi money verbs use maxRetries: 0).
 *
 * Loyalty redemption and manual card entry intentionally live in the
 * Payments console — the hint at the bottom points staff there; the
 * invoice is already in that queue.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
// Ted #22 (#983) — the Adjust-price form now lives in its own component so
// the Open Invoices queue can mount the SAME one. Only the API call differs.
import { InvoiceAdjustFields } from './InvoiceAdjustFields.js';
// Ted #34 E6.3 (#1161) — "add 15%": the tip as a percentage of the service.
import {
  TIP_PERCENT_PRESETS,
  centsToTipInput,
  tipBaseCents,
  tipCentsForPercent,
} from './tip-percent.js';
import {
  adminPaymentsApi,
  readSession,
  type AdminCartAgreement,
  type AdminInvoiceAdjustment,
  type AdminInvoiceAdjustmentInput,
  type AdminOpenInvoice,
  type AdminPackageCoverOptions,
  type AdminPackageCoverPackage,
} from '../../lib/api.js';
// Ted #35 (#1190 story 35.3) — the words for a client's agreed price, shared
// with the profile card so both surfaces say the same thing.
import { agreementOfferLabel, agreementWhyNotCopy } from '../customers/rate-agreement-copy.js';
import { formatServiceDay } from '../../lib/outstanding.js';
import { effectiveRolesOf, rolesHavePermission } from '../../lib/rbac-matrix.js';
import { CardOnFilePayment } from '../payments/CardOnFilePayment.js';
import { defaultPackageLabel } from './PackageBalance.js';
import { CheckNumberField } from './CheckNumberField.js'; // Sawyer 05 end-of-day-tenders (C4)
import './SettleNowDialog.css';

export type SettleNowResult = 'settled' | 'queued';

// #812 — the staff-pickable desk tenders. 'package'/'other' are not offered
// here: package coverage settles upstream in the Bookings orchestrator, and
// "other" is deliberately absent so staff name a real tender (the server
// refuses a desk settle without one).
const DESK_TENDERS = [
  { value: 'cash', label: 'Cash' },
  { value: 'check', label: 'Check' },
  { value: 'external_terminal', label: 'Card terminal' },
] as const;
type DeskTender = (typeof DESK_TENDERS)[number]['value'];

export interface SettleNowDialogProps {
  readonly apiUrl: string;
  /** Booking + invoice context minted by the Bookings orchestrator. */
  readonly bookingId: string;
  readonly bookingLabel: string;
  readonly customerId: string;
  readonly invoiceId: string;
  readonly totalCents: number;
  /** Stable idempotency key for THIS logical settle attempt (#251). */
  readonly idempotencyKey: string;
  /**
   * Ted #10 (#913) — set when the Bookings orchestrator checked out a whole
   * household ("Check out all"): the dogs whose visits were just queued and
   * how many. The dialog then reads as the family's checkout — the cart
   * (every invoice just queued) is what "the visit" means, collected
   * together by default.
   */
  readonly household?: {
    readonly dogNames: readonly string[];
    readonly visits: number;
  };
  /**
   * Close handler. `settled` ⇒ money recorded (Payment row exists);
   * `queued` ⇒ staff deferred — the invoice stays in the end-of-day queue.
   */
  readonly onClose: (result: SettleNowResult) => void;
}

/** "Hazel", "Hazel & Hank", "Hazel, Hank & Skipper". */
function joinDogNames(names: readonly string[]): string {
  if (names.length <= 1) return names[0] ?? '';
  return `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`;
}

/** Ted #34 E6.2 / #948 — the owner's discount lines the desk can take back off. */
type RemovableRule = 'first_groom' | 'multi_dog';
const RULE_COPY: Record<RemovableRule, { testId: string; question: string; placeholder: string }> = {
  first_groom: {
    testId: 'first-groom',
    question: 'Why is this not their first groom?',
    placeholder: 'e.g. groomed here before we used this system',
  },
  multi_dog: {
    testId: 'multi-dog',
    question: 'Why should this visit not get the multi-dog discount?',
    placeholder: 'e.g. not the same family — the dogs just share an account',
  },
};

type Method = 'choose' | 'card' | 'desk';

type DeskState =
  | { kind: 'idle' }
  | { kind: 'settling' }
  | { kind: 'error'; message: string };

type Outcome =
  | { kind: 'pending' }
  | { kind: 'paid'; detail: string };

function formatMoney(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function SettleNowDialog({
  apiUrl,
  bookingId,
  bookingLabel,
  customerId,
  invoiceId,
  totalCents,
  idempotencyKey,
  household,
  onClose,
}: SettleNowDialogProps): JSX.Element {
  const [method, setMethod] = useState<Method>('choose');
  const [desk, setDesk] = useState<DeskState>({ kind: 'idle' });
  // #812 — which tender the desk settle records. No default: the server
  // 400s a desk settle without a method, so the confirm stays disabled
  // until staff pick one.
  const [deskTender, setDeskTender] = useState<DeskTender | null>(null);
  const [checkNumber, setCheckNumber] = useState(''); // Sawyer 05 end-of-day-tenders (C4) — optional, Check only
  // #813 — the owner's OTHER open invoices (the visit cart). null until the
  // read resolves; a failed/404 read (older server) means NO cart section —
  // the single-invoice flow is unchanged, never a dead affordance.
  const [cart, setCart] = useState<{
    invoices: AdminOpenInvoice[];
    totalCents: number;
  } | null>(null);
  const [collectAll, setCollectAll] = useState(false);
  // Ted #7 (#910) — the owner's store credit, read with the cart. null until
  // the read resolves (or when the server cannot say) → no credit UI, never
  // "$0.00 credit". Applied by DEFAULT: it is the customer's own money and
  // leaving it unused is the surprising path; staff untick to keep it on the
  // account. A partial credit names the tender for the remainder.
  const [storeCreditCents, setStoreCreditCents] = useState<number | null>(null);
  const [applyCredit, setApplyCredit] = useState(true);
  // Ted #3 + #9 (#906 #912) — the amount due can change here (a grandfathered
  // rate, the multi-dog 10 %, a correction). `amountDueCents` is the live
  // number; `totalCents` (the prop) is what the orchestrator handed over.
  // The adjustment rows come back with the cart read and after each apply.
  const [amountDueCents, setAmountDueCents] = useState(totalCents);
  const [adjustments, setAdjustments] = useState<AdminInvoiceAdjustment[]>([]);
  const [adjustOpen, setAdjustOpen] = useState(false);
  const [adjustKind, setAdjustKind] = useState<AdminInvoiceAdjustmentInput['kind']>('percent');
  const [adjustValue, setAdjustValue] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustState, setAdjustState] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  const [outcome, setOutcome] = useState<Outcome>({ kind: 'pending' });
  // Ted #35 (#1190 story 35.3) — the client's agreed prices, judged by the
  // SERVER against this cart and read with it. Never applied on their own:
  // the desk taps Apply (Ted's review-first rule). `serviceDateIso` is this
  // invoice's visit date so "today is Wed" names the visit's day.
  const [cartAgreements, setCartAgreements] = useState<AdminCartAgreement[]>([]);
  const [serviceDateIso, setServiceDateIso] = useState<string | null>(null);
  const [agreedRateState, setAgreedRateState] = useState<
    { kind: 'idle' } | { kind: 'saving' } | { kind: 'error'; message: string }
  >({ kind: 'idle' });
  // Ted #34 E6.2 (#1161) / #948 — taking an owner's discount (first-groom,
  // multi-dog) back off, with a reason ("groomed here before Gingr"). null =
  // the form is closed. `rule` only picks the wording; the server reads the
  // rule from the row it wrote.
  const [ruleRemoval, setRuleRemoval] = useState<{
    rule: RemovableRule;
    adjustmentId: string;
    reason: string;
    saving: boolean;
    error: string | null;
  } | null>(null);
  // #1157 E2.3 (Ted #32b, Item 32.6) — "Use package" is the FIRST way to
  // pay. `packageCover` is the server's read: which packages could cover this
  // visit, how many days (one per dog), what would still be due. `unknown`
  // = the read failed, which is said out loud (never read as "no package").
  const [packageCover, setPackageCover] = useState<
    | { kind: 'loading' }
    | { kind: 'ready'; options: AdminPackageCoverOptions }
    | { kind: 'unknown' }
  >({ kind: 'loading' });
  const [packageUse, setPackageUse] = useState<
    | { kind: 'idle' }
    | { kind: 'covering' }
    | { kind: 'error'; message: string }
    | { kind: 'partly'; message: string }
  >({ kind: 'idle' });
  const [tipDollars, setTipDollars] = useState('');
  // Ted #34 E6.3 (#1161) — which percentage button filled the tip, if any.
  // Typing in the field clears it: a typed amount is not "15 %", and the
  // server would refuse a percentage the cents do not match.
  const [tipPercent, setTipPercent] = useState<number | null>(null);
  // #251 should-fix 8 — true while the card-on-file charge POST is in
  // flight (reported by CardOnFilePayment).
  const [cardBusy, setCardBusy] = useState(false);
  const dialogRef = useRef<HTMLDivElement>(null);
  const safeCloseRef = useRef<HTMLButtonElement>(null);
  const restoreFocusRef = useRef<HTMLElement | null>(null);

  const parsedTip = parseFloat(tipDollars);
  const tipCents =
    Number.isFinite(parsedTip) && parsedTip >= 0
      ? Math.round(parsedTip * 100)
      : 0;

  const settled = outcome.kind === 'paid';

  // #251 should-fix 8 — a charge/desk-settle is IN FLIGHT: dismissal is
  // locked. Closing mid-charge with the "stays in the queue" notice was
  // FALSE (the in-flight charge can still complete and settle the
  // invoice after the close), so the dialog stays up until the money
  // POST resolves either way.
  const inFlight = cardBusy || desk.kind === 'settling' || packageUse.kind === 'covering';

  // #1157 E2.3 — the package read, once per dialog (and after a cover).
  const loadPackageCover = useCallback(async () => {
    const res = await adminPaymentsApi.packageCover(apiUrl, invoiceId);
    if (res.error || !res.data?.packageCover || !Array.isArray(res.data.packageCover.packages)) {
      setPackageCover({ kind: 'unknown' });
      return;
    }
    setPackageCover({ kind: 'ready', options: res.data.packageCover });
  }, [apiUrl, invoiceId]);
  useEffect(() => {
    void loadPackageCover();
  }, [loadPackageCover]);

  // #813 — load the owner's cart once per dialog. Only a cart with MORE
  // than this booking's invoice offers collect-together (default ON —
  // Ted's rollup expectation; staff can untick to settle just this one).
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await adminPaymentsApi.getCart(apiUrl, customerId);
      if (cancelled || res.error || !res.data) return;
      // Ted #7 (#910) — store credit rides the same read. Only a finite
      // number counts; null/absent (older server, or unknown) keeps the
      // credit UI hidden rather than claiming there is none.
      if (
        typeof res.data.walletBalanceCents === 'number' &&
        Number.isFinite(res.data.walletBalanceCents)
      ) {
        setStoreCreditCents(res.data.walletBalanceCents);
      }
      // Defensive: an older server (404 → error path) never reaches here,
      // but a malformed 200 must degrade to "no cart", not crash.
      const invoices = Array.isArray(res.data.invoices) ? res.data.invoices : [];
      // Ted #35 — the agreed prices ride the same read (absent on an older server).
      setCartAgreements(Array.isArray(res.data.agreements) ? res.data.agreements : []);
      // Ted #3 + #9 — this invoice as the server holds it (adjustments so far).
      const mine = invoices.find((i) => i.id === invoiceId);
      if (mine) {
        if (Array.isArray(mine.adjustments)) setAdjustments(mine.adjustments);
        if (typeof mine.totalCents === 'number') setAmountDueCents(mine.totalCents);
        if (typeof mine.serviceDate === 'string' && mine.serviceDate) setServiceDateIso(mine.serviceDate);
      }
      const others = invoices.filter((i) => i.id !== invoiceId);
      if (others.length > 0 && typeof res.data.totalCents === 'number') {
        setCart({ invoices, totalCents: res.data.totalCents });
        setCollectAll(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiUrl, customerId, invoiceId]);

  const collectingCart = collectAll && cart !== null;
  const deskAmountCents = collectingCart ? cart.totalCents : amountDueCents;
  const originalTotalCents = adjustments[0]?.originalTotalCents ?? amountDueCents;
  // Ted #34 E6.3 — the service the tip percentages are OF (not the meal or
  // late-pickup lines the server added on top). See tip-percent.ts.
  const tipServiceCents = tipBaseCents(amountDueCents, adjustments);
  // A chosen percentage follows the amount due: a price adjusted after the
  // tap re-derives the tip rather than leaving a stale "15 %" behind.
  useEffect(() => {
    if (tipPercent === null) return;
    setTipDollars(centsToTipInput(tipCentsForPercent(tipServiceCents, tipPercent)));
  }, [tipPercent, tipServiceCents]);

  // Ted #7 (#910) — the store-credit split for the desk leg.
  const creditAvailableCents =
    storeCreditCents !== null && storeCreditCents > 0 ? storeCreditCents : 0;
  const creditWouldApplyCents = Math.min(creditAvailableCents, deskAmountCents);
  const appliedCreditCents = applyCredit ? creditWouldApplyCents : 0;
  const remainderCents = deskAmountCents - appliedCreditCents;
  const creditCoversAll = appliedCreditCents > 0 && remainderCents === 0;

  // Ted #3 follow-up (#906) review fix — which invoices a PERCENT actually
  // moves while the whole cart is being collected. A percent on a $0 invoice
  // is `delta = 0 % of $0 = $0`, which `decideAdjustment` refuses as
  // `no_change` — and the apply loop BREAKS on the first refusal, so a cart
  // holding any unpriced sibling used to leave the family half-discounted
  // (applied to 1 of 3). Sending a request that is guaranteed to be refused
  // is the bug; dropping those ids is not hiding anything. A cart read that
  // somehow omits this booking still adjusts it.
  const percentTargetIds = useMemo(() => {
    if (!collectingCart || cart === null) return [invoiceId];
    const priced = cart.invoices.filter((i) => i.totalCents > 0).map((i) => i.id);
    return priced.includes(invoiceId) ? priced : [invoiceId, ...priced];
  }, [collectingCart, cart, invoiceId]);

  const handleDismiss = useCallback(() => {
    if (inFlight) return;
    onClose(settled ? 'settled' : 'queued');
  }, [onClose, settled, inFlight]);

  useEffect(() => {
    restoreFocusRef.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    safeCloseRef.current?.focus();
    return () => restoreFocusRef.current?.focus();
  }, []);

  const handleDialogKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>): void => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        handleDismiss();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = dialogRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (!focusable || focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [handleDismiss],
  );

  // Ted #3 + #9 (#906 #912) — apply a price adjustment. A percent while
  // collecting the whole cart applies to EVERY visit in it (the multi-dog
  // case: two siblings, 10 % off both); an amount or a new total applies to
  // this visit only. Then the cart is re-read so every number on screen is
  // the server's. A refusal (band, below zero, no change) writes nothing.
  const handleApplyAdjustment = useCallback(async () => {
    const reason = adjustReason.trim();
    const parsed = Number(adjustValue);
    if (!Number.isFinite(parsed) || reason.length < 2) return;
    const value = adjustKind === 'percent' ? Math.round(parsed) : Math.round(parsed * 100);
    const body: AdminInvoiceAdjustmentInput = { kind: adjustKind, value, reason };
    const targets = adjustKind === 'percent' ? percentTargetIds : [invoiceId];
    setAdjustState({ kind: 'saving' });
    let applied = 0;
    let failure: string | null = null;
    for (const id of targets) {
      const res = await adminPaymentsApi.adjustOpenInvoice(apiUrl, id, body);
      if (res.error || !res.data?.invoice) {
        failure = res.error ?? 'Could not record the adjustment.';
        break;
      }
      applied += 1;
      if (id === invoiceId) {
        setAmountDueCents(res.data.invoice.totalCents);
        if (Array.isArray(res.data.invoice.adjustments)) setAdjustments(res.data.invoice.adjustments);
      }
    }
    // Re-read the cart so its total + lines reflect every applied row.
    const cartRes = await adminPaymentsApi.getCart(apiUrl, customerId);
    if (!cartRes.error && cartRes.data && Array.isArray(cartRes.data.invoices)) {
      const invoices = cartRes.data.invoices;
      const mine = invoices.find((i) => i.id === invoiceId);
      if (mine) {
        setAmountDueCents(mine.totalCents);
        if (Array.isArray(mine.adjustments)) setAdjustments(mine.adjustments);
      }
      if (cart !== null && typeof cartRes.data.totalCents === 'number') {
        setCart({ invoices, totalCents: cartRes.data.totalCents });
      }
      setCartAgreements(Array.isArray(cartRes.data.agreements) ? cartRes.data.agreements : []);
    }
    if (failure) {
      setAdjustState({
        kind: 'error',
        message:
          targets.length > 1
            ? `${failure} (applied to ${applied} of ${targets.length} visits — the rest are unchanged.)`
            : failure,
      });
      return;
    }
    setAdjustState({ kind: 'idle' });
    setAdjustOpen(false);
    setAdjustValue('');
    setAdjustReason('');
  }, [apiUrl, customerId, invoiceId, adjustKind, adjustValue, adjustReason, percentTargetIds, cart]);

  // Ted #34 E6.2 (#1161) / #948 — remove an owner's discount. The server
  // reverses exactly the row it wrote; then the cart is re-read so every
  // number on screen is the server's.
  const handleRemoveRule = useCallback(async () => {
    if (!ruleRemoval) return;
    const reason = ruleRemoval.reason.trim();
    if (reason.length < 2) return;
    setRuleRemoval({ ...ruleRemoval, saving: true, error: null });
    const res = await adminPaymentsApi.removeFirstGroomDiscount(
      apiUrl,
      invoiceId,
      ruleRemoval.adjustmentId,
      reason,
    );
    if (res.error || !res.data?.invoice) {
      setRuleRemoval({
        ...ruleRemoval,
        saving: false,
        error: res.error ?? 'Could not remove the discount. Nothing changed.',
      });
      return;
    }
    setAmountDueCents(res.data.invoice.totalCents);
    if (Array.isArray(res.data.invoice.adjustments)) setAdjustments(res.data.invoice.adjustments);
    const cartRes = await adminPaymentsApi.getCart(apiUrl, customerId);
    if (!cartRes.error && cartRes.data && Array.isArray(cartRes.data.invoices) && cart !== null) {
      if (typeof cartRes.data.totalCents === 'number') {
        setCart({ invoices: cartRes.data.invoices, totalCents: cartRes.data.totalCents });
      }
    }
    setRuleRemoval(null);
  }, [apiUrl, invoiceId, customerId, cart, ruleRemoval]);
  const firstGroomRemoved = adjustments.some((a) => a.kind === 'first_groom_removed');
  // #948 — a multi-dog line is removable until the desk takes it off, or a
  // later price (an override, the agreed rate, a package) replaces the total
  // it discounted — the server refuses that removal too.
  const multiDogRemovable =
    !adjustments.some((a) => a.kind === 'multi_dog_removed') &&
    !adjustments.some((a) => a.kind === 'override' || a.kind === 'agreed_rate' || a.kind === 'package');

  // Ted #35 (#1190 story 35.3) — Apply the client's agreed rate to every
  // invoice in this checkout it fits (a two-dog deal is two invoices, one per
  // Gingr booking). The server prices each one; nothing about the amount is
  // sent from here. Then the cart is re-read so every number on screen is
  // the server's. A refusal on one visit stops the loop and says how far it got.
  const handleApplyAgreedRate = useCallback(
    async (offer: AdminCartAgreement) => {
      const targets = offer.invoices.filter((i) => i.applicable).map((i) => i.invoiceId);
      if (targets.length === 0) return;
      setAgreedRateState({ kind: 'saving' });
      let applied = 0;
      let failure: string | null = null;
      for (const id of targets) {
        const res = await adminPaymentsApi.applyAgreedRate(apiUrl, id, offer.agreement.id);
        if (res.error || !res.data?.invoice) {
          failure = res.error ?? 'Could not apply the agreed rate. Nothing changed.';
          break;
        }
        applied += 1;
        if (id === invoiceId) {
          setAmountDueCents(res.data.invoice.totalCents);
          if (Array.isArray(res.data.invoice.adjustments)) setAdjustments(res.data.invoice.adjustments);
        }
      }
      const cartRes = await adminPaymentsApi.getCart(apiUrl, customerId);
      if (!cartRes.error && cartRes.data && Array.isArray(cartRes.data.invoices)) {
        const invoices = cartRes.data.invoices;
        const mine = invoices.find((i) => i.id === invoiceId);
        if (mine) {
          setAmountDueCents(mine.totalCents);
          if (Array.isArray(mine.adjustments)) setAdjustments(mine.adjustments);
        }
        if (cart !== null && typeof cartRes.data.totalCents === 'number') {
          setCart({ invoices, totalCents: cartRes.data.totalCents });
        }
        setCartAgreements(Array.isArray(cartRes.data.agreements) ? cartRes.data.agreements : []);
      }
      if (failure) {
        setAgreedRateState({
          kind: 'error',
          message:
            targets.length > 1
              ? `${failure} (applied to ${applied} of ${targets.length} visits — the rest are unchanged.)`
              : failure,
        });
        return;
      }
      setAgreedRateState({ kind: 'idle' });
    },
    [apiUrl, customerId, invoiceId, cart],
  );

  // Ted #3 follow-up (#906) — what the desk can ACTUALLY do when the amount
  // due is $0 (every Gingr-mirrored visit until it is priced). Mirrors the
  // server's policy in `payments-admin/service.ts` `decideAdjustment` rather
  // than guessing at it, with the current total at 0:
  //   • percent    → delta = 0 % of $0 = $0 → refused `no_change`;
  //   • a decrease → the amount due goes negative → refused `below_zero`;
  //   • anything else non-zero → |delta| > the desk band, which is 20 % of
  //     $0 = $0, so it ALSO needs `payments:refund:all` (owner / manager).
  // So a receptionist can move nothing on a $0 invoice, and everyone's
  // percent is a no-op. Previously the form offered all of it and answered
  // with a 403 — Ted could not tell why his 25 % would not take.
  const canExceedDeskBand = useMemo(
    () =>
      rolesHavePermission(
        effectiveRolesOf(readSession().staff),
        'payments:refund:all',
      ),
    [],
  );
  const zeroAmountDue = amountDueCents === 0;
  /** $0 AND no authority past the band ⇒ every path is refused. */
  const adjustLocked = zeroAmountDue && !canExceedDeskBand;
  // Review fix — $0 has two causes and they are NOT the same sentence. A
  // Gingr-mirrored visit was never priced; a visit an owner comped to $0 was
  // priced deliberately, and the adjustment ledger rendered right above this
  // note says so. Telling Ted his own comp "has no price on it yet" (or
  // telling Kylie an owner "has to set the total first" on a total an owner
  // just set) is the one unprovable claim this change existed to remove. The
  // adjustment rows are the evidence, and they come from the server.
  const deliberatelyZero = zeroAmountDue && adjustments.length > 0;
  // Ted #10 (#913) — while the whole cart is being collected, "the visit" is
  // the CART (that is what the header badge counts), so this per-invoice line
  // has to say "booking", the same word the cart lines already use.
  const adjustSubject = collectingCart ? 'booking' : 'visit';

  // The percent option cannot be the selected kind on a $0 visit — it is
  // disabled, so leaving it selected would strand the form on a control the
  // desk cannot use. `New total` is the one that works, so land there.
  useEffect(() => {
    if (zeroAmountDue && adjustKind === 'percent') setAdjustKind('override');
  }, [zeroAmountDue, adjustKind]);


  // Cash / check / external-terminal money collected at the desk. The settle
  // is atomic with its REAL Payment ledger row server-side (#248) — a failure
  // here means NO money was recorded and the invoice is still open. #812:
  // the chosen tender rides the settle and lands on the ledger row.
  const handleDeskSettle = useCallback(async () => {
    // Ted #7 (#910) — credit covering the whole amount IS the tender; a
    // partial credit still needs staff to name how the remainder was paid.
    if (!creditCoversAll && !deskTender) return;
    setDesk({ kind: 'settling' });
    const tender = creditCoversAll ? 'store_credit' : (deskTender as DeskTender);
    const tenderLabel = creditCoversAll
      ? 'store credit'
      : (DESK_TENDERS.find((t) => t.value === deskTender)?.label ?? String(deskTender));
    // How the money was split, in the success line — the desk should be able
    // to read back exactly what the ledger now says.
    const splitDetail =
      appliedCreditCents > 0 && !creditCoversAll
        ? `${formatMoney(appliedCreditCents)} store credit + ${formatMoney(remainderCents)} ${tenderLabel}`
        : tenderLabel;
    // Sawyer 05 end-of-day-tenders (C4) — the number rides only a check, and
    // the success line names only the number the server says it STORED (an
    // older server, or the link arm, stores none: the line then says "Check").
    const cn = tender === 'check' ? checkNumber.trim() : '';
    const detailFor = (stored: string | null | undefined): string => {
      if (tender !== 'check' || !stored) return splitDetail;
      const checkLabel = `Check #${stored}`;
      return appliedCreditCents > 0
        ? `${formatMoney(appliedCreditCents)} store credit + ${formatMoney(remainderCents)} ${checkLabel}`
        : checkLabel;
    };
    // #813 — collect-together settles the WHOLE cart atomically into ONE
    // payment; the server re-derives the total inside the transaction and
    // refuses a stale cart before anything flips.
    if (collectAll && cart !== null) {
      const res = await adminPaymentsApi.settleCart(
        apiUrl,
        cart.invoices.map((i) => i.id),
        tender,
        cart.totalCents,
        appliedCreditCents,
        cn || undefined, // Sawyer 05 end-of-day-tenders (C4)
      );
      if (res.error || !res.data?.payment) {
        setDesk({
          kind: 'error',
          message:
            res.error ??
            'Could not record the payment. The invoices are still open in the end-of-day queue.',
        });
        return;
      }
      setDesk({ kind: 'idle' });
      setOutcome({
        kind: 'paid',
        detail: `${formatMoney(res.data.payment.totalCents)} recorded as paid — ${detailFor(res.data.payment.checkNumber)} — covering ${cart.invoices.length} services in one payment.`,
      });
      return;
    }
    const res = await adminPaymentsApi.settleOpenInvoice(apiUrl, invoiceId, {
      paymentMethod: tender,
      ...(appliedCreditCents > 0 ? { walletAmountCents: appliedCreditCents } : {}),
      ...(cn ? { checkNumber: cn } : {}), // Sawyer 05 end-of-day-tenders (C4)
    });
    if (res.error || !res.data?.invoice) {
      setDesk({
        kind: 'error',
        message:
          res.error ??
          'Could not record the payment. The invoice is still open in the end-of-day queue.',
      });
      return;
    }
    setDesk({ kind: 'idle' });
    setOutcome({
      kind: 'paid',
      detail: `${formatMoney(amountDueCents)} recorded as paid — ${detailFor(res.data.payment?.checkNumber)}.`,
    });
  }, [
    apiUrl,
    invoiceId,
    amountDueCents,
    deskTender,
    collectAll,
    cart,
    appliedCreditCents,
    remainderCents,
    creditCoversAll,
    checkNumber,
  ]);

  // #1157 E2.3 — "Use package": the server draws one day per dog and takes
  // the visit off the bill in ONE transaction; when nothing is left due the
  // $0 invoice is recorded with the package as its tender (the same settle
  // the checkout's covered path uses). A residual (meal tracking, a late
  // charge) stays due and the desk collects it below.
  const handleUsePackage = useCallback(
    async (pkg: AdminPackageCoverPackage) => {
      setPackageUse({ kind: 'covering' });
      const res = await adminPaymentsApi.coverWithPackage(apiUrl, invoiceId, pkg.id);
      if (res.error || !res.data?.invoice) {
        setPackageUse({
          kind: 'error',
          message: res.error ?? 'Could not use the package. Nothing was used and the visit is unchanged.',
        });
        void loadPackageCover();
        return;
      }
      const { invoice, package: after, days } = res.data;
      const used = `${days} package day${days === 1 ? '' : 's'} used — ${after.remainingDays} left`;
      if (Array.isArray(invoice.adjustments)) setAdjustments(invoice.adjustments);
      setAmountDueCents(invoice.totalCents);
      if (invoice.totalCents > 0) {
        setPackageUse({
          kind: 'partly',
          message: `${used}. ${formatMoney(invoice.totalCents)} is still due for the lines a package does not cover.`,
        });
        void loadPackageCover();
        return;
      }
      const settledRes = await adminPaymentsApi.settleOpenInvoice(apiUrl, invoiceId, { paymentMethod: 'package' });
      const others = cart ? cart.invoices.filter((i) => i.id !== invoiceId).length : 0;
      const tail =
        others > 0
          ? ` The other ${others} ${others === 1 ? 'visit is' : 'visits are'} still in the open-invoices queue.`
          : '';
      setPackageUse({ kind: 'idle' });
      setOutcome({
        kind: 'paid',
        detail:
          settledRes.error || !settledRes.data?.invoice
            ? `Covered by package — ${used}. Nothing is due, but the $0 invoice could not be marked settled; it stays in the queue (${settledRes.error ?? 'unknown error'}).${tail}`
            : `Covered by package — ${used}. Nothing to collect for this visit.${tail}`,
      });
    },
    [apiUrl, invoiceId, cart, loadPackageCover],
  );

  // Card-on-file success: the server already settled the open invoice
  // atomically with the Payment row (completeDeskPayment, #248) — nothing
  // more to do client-side.
  const handleCardSuccess = useCallback(
    (paymentId: string) => {
      // Money moved — the charge is no longer in flight (defensive: the
      // unmount cleanup in CardOnFilePayment also releases the lock).
      setCardBusy(false);
      setOutcome({
        kind: 'paid',
        detail: `Card charged — payment ${paymentId}. The invoice was settled automatically.`,
      });
    },
    [],
  );

  return (
    <div
      className="ferro-settle-now__overlay"
      data-testid="settle-now-overlay"
    >
      <button
        type="button"
        aria-label="Close payment collection"
        data-testid="settle-now-backdrop"
        onClick={handleDismiss}
        disabled={inFlight}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          padding: 0,
          border: 0,
          background: 'transparent',
          cursor: 'default',
        }}
      />
      <div
        ref={dialogRef}
        className="ferro-settle-now__panel"
        role="dialog"
        aria-modal="true"
        aria-label={`Collect payment for booking ${bookingLabel}`}
        data-testid="settle-now-dialog"
        onKeyDown={handleDialogKeyDown}
        style={{ position: 'relative' }}
      >
        <header className="ferro-card__header ferro-settle-now__header">
          <h2 className="ferro-section__subtitle">Collect payment</h2>
          {/* Ted #10 (#913) — the number about to be collected: the whole
              cart while "Collect the whole visit" is on, else this invoice.
              One glance, one truth. */}
          <span
            className="ferro-badge ferro-badge--neutral"
            data-testid="settle-now-total"
          >
            {formatMoney(deskAmountCents)}
          </span>
        </header>
        {/* Ted #3 + #9 (#906 #912) — every price change and its reason, in
            the order they were made; the ledger will say the same. */}
        {adjustments.length > 0 ? (
          <ul className="ferro-settle-now__adjustments" data-testid="settle-now-adjustments">
            <li className="ferro-muted">
              Was {formatMoney(originalTotalCents)} → now {formatMoney(amountDueCents)}
            </li>
            {adjustments.map((a) => (
              <li key={a.id} data-testid={`settle-now-adjustment-${a.id}`}>
                {a.deltaCents < 0 ? '−' : '+'}
                {formatMoney(Math.abs(a.deltaCents))}
                {a.kind === 'percent' || a.kind === 'first_groom' || a.kind === 'multi_dog'
                  ? ` (${Math.abs(a.value)}%)`
                  : ''}{' '}
                · {a.reason}
                {a.staffUsername ? ` — ${a.staffUsername}` : ''}
                {/* Ted #34 E6.2 / #948 — the owner's rules, removable with a reason. */}
                {((a.kind === 'first_groom' && !firstGroomRemoved) || (a.kind === 'multi_dog' && multiDogRemovable)) &&
                !settled &&
                ruleRemoval === null ? (
                  <>
                    {' '}
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost ferro-settle-now__remove-rule"
                      onClick={() =>
                        setRuleRemoval({
                          rule: a.kind === 'multi_dog' ? 'multi_dog' : 'first_groom',
                          adjustmentId: a.id,
                          reason: '',
                          saving: false,
                          error: null,
                        })
                      }
                      disabled={inFlight}
                      data-testid={`settle-now-${a.kind === 'multi_dog' ? 'multi-dog' : 'first-groom'}-remove`}
                    >
                      Remove
                    </button>
                  </>
                ) : null}
              </li>
            ))}
          </ul>
        ) : null}
        {ruleRemoval ? (
          <div
            className="ferro-settle-now__adjust-form"
            data-testid={`settle-now-${RULE_COPY[ruleRemoval.rule].testId}-remove-form`}
          >
            <label className="ferro-field">
              <span className="ferro-field__label">{RULE_COPY[ruleRemoval.rule].question}</span>
              <input
                type="text"
                className="ferro-field__input"
                value={ruleRemoval.reason}
                onChange={(e) => setRuleRemoval({ ...ruleRemoval, reason: e.target.value, error: null })}
                placeholder={RULE_COPY[ruleRemoval.rule].placeholder}
                disabled={ruleRemoval.saving}
                data-testid={`settle-now-${RULE_COPY[ruleRemoval.rule].testId}-remove-reason`}
              />
            </label>
            {ruleRemoval.error ? (
              <div
                className="ferro-dashboard__error"
                role="alert"
                data-testid={`settle-now-${RULE_COPY[ruleRemoval.rule].testId}-remove-error`}
              >
                <p>{ruleRemoval.error}</p>
              </div>
            ) : null}
            <div className="ferro-settle-now__actions">
              <button
                type="button"
                className="ferro-btn ferro-btn--primary"
                onClick={() => void handleRemoveRule()}
                disabled={ruleRemoval.saving || ruleRemoval.reason.trim().length < 2}
                data-testid={`settle-now-${RULE_COPY[ruleRemoval.rule].testId}-remove-confirm`}
              >
                {ruleRemoval.saving ? 'Removing…' : 'Remove the discount'}
              </button>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost"
                onClick={() => setRuleRemoval(null)}
                disabled={ruleRemoval.saving}
                data-testid={`settle-now-${RULE_COPY[ruleRemoval.rule].testId}-remove-cancel`}
              >
                Keep it
              </button>
            </div>
          </div>
        ) : null}

        {household ? (
          <p
            className="ferro-muted ferro-settle-now__meta"
            data-testid="settle-now-household-meta"
          >
            {joinDogNames(household.dogNames)} checked out — {household.visits}{' '}
            {household.visits === 1 ? 'visit is' : 'visits are'} in the
            open-invoices queue. Collect them together now, or settle at end
            of day.
          </p>
        ) : (
          <p className="ferro-muted ferro-settle-now__meta">
            Booking {bookingLabel} · checked out. The visit is recorded in the
            open-invoices queue — collect now, or settle it at end of day.
          </p>
        )}
        {/* Ted #7 (#910) — the credit is visible BEFORE any tender is picked,
            so the desk never collects the full amount and refunds later. */}
        {creditAvailableCents > 0 && !settled ? (
          <p
            className="ferro-settle-now__credit"
            data-testid="settle-now-store-credit"
          >
            Store credit on this account: <strong>{formatMoney(creditAvailableCents)}</strong>
            {method !== 'desk'
              ? ' — apply it under "Record as paid".'
              : null}
          </p>
        ) : null}

        {settled ? (
          <div
            className="ferro-form__success ferro-settle-now__success"
            role="status"
            data-testid="settle-now-success"
          >
            <p>{outcome.kind === 'paid' ? outcome.detail : null}</p>
            <button
              type="button"
              className="ferro-btn ferro-btn--primary"
              onClick={handleDismiss}
              data-testid="settle-now-done"
            >
              Done
            </button>
          </div>
        ) : (
          <>
            {method === 'choose' ? (
              <div
                className="ferro-settle-now__methods"
                role="group"
                aria-label="Payment options"
              >
                {/* #1157 E2.3 — the package FIRST: "Use package (33 left)". */}
                <PackageChoices
                  state={packageCover}
                  use={packageUse}
                  disabled={inFlight}
                  collectingCart={collectingCart}
                  onUse={(p) => void handleUsePackage(p)}
                  onRetry={() => {
                    setPackageCover({ kind: 'loading' });
                    void loadPackageCover();
                  }}
                />
                {/* Ted #10 (#913) — card on file charges ONE booking (the
                    cart-on-card slice is #579). While the whole visit is
                    being collected the option is off and says why, so staff
                    never think three dogs were charged when one was. The
                    escape hatch narrows the collection to this visit first. */}
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary"
                  onClick={() => setMethod('card')}
                  disabled={collectingCart}
                  aria-describedby={collectingCart ? 'settle-now-card-cart-note' : undefined}
                  data-testid="settle-now-method-card"
                >
                  Charge card on file
                </button>
                {collectingCart && cart ? (
                  <>
                    <p
                      id="settle-now-card-cart-note"
                      className="ferro-field__hint ferro-settle-now__card-note"
                      data-testid="settle-now-card-cart-note"
                    >
                      Card on file collects one visit at a time — record the
                      whole visit as paid at the desk, or settle later.
                    </p>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      onClick={() => {
                        setCollectAll(false);
                        setMethod('card');
                      }}
                      data-testid="settle-now-card-this-visit"
                    >
                      Charge card for this visit only ({formatMoney(amountDueCents)})
                    </button>
                  </>
                ) : null}
                <button
                  type="button"
                  className="ferro-btn ferro-btn--primary"
                  onClick={() => setMethod('desk')}
                  data-testid="settle-now-method-desk"
                >
                  Record as paid (cash / external)
                </button>
                <button
                  type="button"
                  ref={safeCloseRef}
                  className="ferro-btn ferro-btn--ghost"
                  onClick={handleDismiss}
                  disabled={inFlight}
                  data-testid="settle-now-settle-later"
                >
                  Settle later (keep in queue)
                </button>
              </div>
            ) : null}

            {/* Ted #35 (#1190 story 35.3) — the client's agreed price on file,
                offered ABOVE "Adjust price" so the deal is one tap, never
                re-typed from memory. When it does not fit today it stays on
                screen, greyed, with the reason — the desk should never wonder
                why the $70 is missing. Review-first: nothing applies itself. */}
            {method === 'choose' && !settled && cartAgreements.length > 0 ? (
              <div className="ferro-settle-now__agreed" data-testid="settle-agreed-rate">
                {cartAgreements.map((offer) => {
                  const mine = offer.invoices.find((i) => i.invoiceId === invoiceId) ?? offer.invoices[0];
                  const targets = offer.invoices.filter((i) => i.applicable);
                  if (offer.applicable && targets.length > 0) {
                    return (
                      <div
                        key={offer.agreement.id}
                        className="ferro-settle-now__agreed-offer"
                        data-testid={`settle-agreed-rate-offer-${offer.agreement.id}`}
                      >
                        <div>
                          <strong>{agreementOfferLabel(offer.agreement)}</strong>
                          <div className="ferro-muted ferro-settle-now__agreed-note">
                            {offer.agreement.note}
                            {targets.length > 1 ? ` · applies to ${targets.length} visits in this checkout` : ''}
                          </div>
                        </div>
                        <button
                          type="button"
                          className="ferro-btn ferro-btn--outline"
                          onClick={() => void handleApplyAgreedRate(offer)}
                          disabled={inFlight || agreedRateState.kind === 'saving'}
                          data-testid="settle-agreed-rate-apply"
                        >
                          {agreedRateState.kind === 'saving' ? 'Applying…' : 'Apply'}
                        </button>
                      </div>
                    );
                  }
                  const whyNot = mine?.whyNot ?? null;
                  if (!whyNot || whyNot === 'inactive') return null;
                  const dateOf =
                    cart?.invoices.find((i) => i.id === (mine?.invoiceId ?? invoiceId))?.serviceDate ?? serviceDateIso;
                  return (
                    <p
                      key={offer.agreement.id}
                      className="ferro-muted ferro-settle-now__agreed-off"
                      data-testid={`settle-agreed-rate-off-${offer.agreement.id}`}
                    >
                      {agreementWhyNotCopy(offer.agreement, whyNot, dateOf)}
                    </p>
                  );
                })}
                {agreedRateState.kind === 'error' ? (
                  <p className="ferro-dashboard__error" role="alert" data-testid="settle-agreed-rate-error">
                    {agreedRateState.message}
                  </p>
                ) : null}
              </div>
            ) : null}
            {/* Ted #3 + #9 (#906 #912) — change the price BEFORE collecting:
                a grandfathered rate, the multi-dog 10 %, a correction. Reason
                required; the server refuses beyond the desk band without an
                owner/manager. */}
            {method === 'choose' ? (
              <div className="ferro-settle-now__adjust" data-testid="settle-now-adjust">
                {!adjustOpen ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={() => setAdjustOpen(true)}
                    disabled={inFlight}
                    data-testid="settle-now-adjust-open"
                  >
                    Adjust price
                  </button>
                ) : (
                  <InvoiceAdjustFields
                    idPrefix="settle-now-adjust"
                    kind={adjustKind}
                    value={adjustValue}
                    reason={adjustReason}
                    saving={adjustState.kind === 'saving'}
                    error={adjustState.kind === 'error' ? adjustState.message : null}
                    amountDueCents={amountDueCents}
                    subject={adjustSubject}
                    locked={adjustLocked}
                    deliberatelyZero={deliberatelyZero}
                    hintTestId="settle-now-adjust-cart-hint"
                    hint={
                      /* Review fix — the count has to be what the apply loop
                         will actually send, not the cart size: an unpriced
                         sibling is dropped (the server refuses it `no_change`
                         and the loop would break on it). */
                      adjustKind === 'percent' && collectingCart && cart
                        ? percentTargetIds.length === cart.invoices.length
                          ? `Applies to all ${cart.invoices.length} visits in this checkout.`
                          : percentTargetIds.length > 1
                            ? `Applies to the ${percentTargetIds.length} priced visits in this checkout — the rest have no price on them yet.`
                            : 'Applies to this booking only — the other visits in this checkout have no price on them yet.'
                        : null
                    }
                    onKindChange={setAdjustKind}
                    onValueChange={setAdjustValue}
                    onReasonChange={setAdjustReason}
                    onApply={() => void handleApplyAdjustment()}
                    onCancel={() => {
                      setAdjustOpen(false);
                      setAdjustState({ kind: 'idle' });
                    }}
                  />
                )}
              </div>
            ) : null}

            {method === 'card' && collectingCart ? (
              // Defensive: the choose step disables the card button while
              // collecting the cart, so this renders only if state drifts.
              <div className="ferro-settle-now__card-leg">
                <p className="ferro-field__hint" data-testid="settle-now-card-cart-note">
                  Card on file collects one visit at a time — record the whole
                  visit as paid at the desk, or settle later.
                </p>
                <button
                  type="button"
                  className="ferro-btn ferro-btn--ghost"
                  onClick={() => setMethod('choose')}
                  data-testid="settle-now-card-back"
                >
                  Back
                </button>
              </div>
            ) : null}
            {method === 'card' && !collectingCart ? (
              <div className="ferro-settle-now__card-leg">
                {/* Ted #34 E6.3 (#1161) — "add 15%": one tap fills the tip
                    with that percentage of the SERVICE (never the meal or
                    late-pickup lines), shown in dollars on the button before
                    it is tapped. The field stays editable; typing clears the
                    percentage. */}
                <div className="ferro-settle-now__tip" data-testid="settle-now-tip">
                  <p id="settle-now-tip-label" className="ferro-field__label ferro-settle-now__tip-label">
                    Tip — percent of the {formatMoney(tipServiceCents)} service
                  </p>
                  <div
                    className="ferro-settle-now__tip-presets"
                    role="group"
                    aria-labelledby="settle-now-tip-label"
                  >
                    {TIP_PERCENT_PRESETS.map((p) => {
                      const cents = tipCentsForPercent(tipServiceCents, p);
                      const chosen = tipPercent === p;
                      return (
                        <button
                          key={p}
                          type="button"
                          aria-pressed={chosen}
                          className={chosen ? 'ferro-btn ferro-btn--primary' : 'ferro-btn ferro-btn--ghost'}
                          onClick={() => {
                            if (chosen) {
                              // A second tap takes the tip back off.
                              setTipPercent(null);
                              setTipDollars('');
                              return;
                            }
                            setTipPercent(p);
                            setTipDollars(centsToTipInput(cents));
                          }}
                          disabled={cardBusy || tipServiceCents === 0}
                          data-testid={`settle-now-tip-${p}`}
                        >
                          {p}%
                          <span className="ferro-settle-now__tip-amount">{formatMoney(cents)}</span>
                        </button>
                      );
                    })}
                  </div>
                  <label className="ferro-field">
                    <span className="ferro-field__label">Tip (optional)</span>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      className="ferro-field__input"
                      value={tipDollars}
                      onChange={(e) => {
                        setTipPercent(null);
                        setTipDollars(e.target.value);
                      }}
                      placeholder="0.00"
                      disabled={cardBusy}
                      data-testid="settle-now-tip-input"
                    />
                  </label>
                  {tipPercent !== null && tipCents > 0 ? (
                    <p className="ferro-field__hint" role="status" data-testid="settle-now-tip-basis">
                      {tipPercent}% of {formatMoney(tipServiceCents)} = {formatMoney(tipCents)} tip
                    </p>
                  ) : null}
                </div>
                <CardOnFilePayment
                  apiUrl={apiUrl}
                  customerId={customerId}
                  bookingId={bookingId}
                  totalCents={amountDueCents}
                  tipCents={tipCents}
                  tipPercent={tipPercent}
                  idempotencyKey={idempotencyKey}
                  onSuccess={handleCardSuccess}
                  onCancel={() => setMethod('choose')}
                  onBusyChange={setCardBusy}
                />
              </div>
            ) : null}

            {method === 'desk' ? (
              <div className="ferro-settle-now__desk-leg">
                <p>
                  Record {formatMoney(deskAmountCents)} as collected at the
                  desk. Pick how it was paid — the tender is written onto the
                  payment record (that's what end-of-day and accounting
                  reports count).
                </p>
                {cart !== null ? (
                  <div
                    className="ferro-settle-now__cart"
                    data-testid="settle-now-cart"
                  >
                    <label className="ferro-field ferro-field--inline">
                      <input
                        type="checkbox"
                        checked={collectAll}
                        onChange={(e) => setCollectAll(e.target.checked)}
                        disabled={desk.kind === 'settling'}
                        data-testid="settle-now-cart-toggle"
                      />
                      <span>
                        Collect the whole visit — {cart.invoices.length} services,{' '}
                        {formatMoney(cart.totalCents)} in one payment
                      </span>
                    </label>
                    <ul className="ferro-settle-now__cart-lines">
                      {cart.invoices.map((line) => (
                        <li key={line.id} className="ferro-muted" data-testid={`settle-now-cart-line-${line.id}`}>
                          {/* Ted #23 (#984) — an added service reads as what
                              it is ("Nail Trim"), not as the service type it
                              rides on. Two lines both saying "Grooming" was
                              the non-itemization he reported. */}
                          {line.serviceLegLabel ?? line.serviceTypeName}
                          {line.dogNames && line.dogNames.length > 0
                            ? ` — ${line.dogNames.join(', ')}`
                            : ''}
                          {/* Ted #29 (#1108) — an added service says WHICH
                              DAY and WHO did it, so a stay with four walks
                              reads as four dated lines, not four "Walk"s. */}
                          {line.parentBookingId && line.serviceDate ? (
                            <span data-testid={`settle-now-cart-line-date-${line.id}`}>
                              {' · '}
                              {formatServiceDay(line.serviceDate.slice(0, 10))}
                            </span>
                          ) : null}
                          {line.parentBookingId && line.performedBy ? (
                            <span data-testid={`settle-now-cart-line-who-${line.id}`}>
                              {' · '}
                              {line.performedBy}
                            </span>
                          ) : null}
                          {' · '}
                          {formatMoney(line.totalCents)}
                          {line.id === invoiceId ? ' (this booking)' : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
                {/* Ted #7 (#910) — apply the owner's store credit first; the
                    tender question is then about the REMAINDER only. */}
                {creditAvailableCents > 0 ? (
                  <div
                    className="ferro-settle-now__credit-leg"
                    data-testid="settle-now-credit"
                  >
                    <label className="ferro-field ferro-field--inline">
                      <input
                        type="checkbox"
                        checked={applyCredit}
                        onChange={(e) => setApplyCredit(e.target.checked)}
                        disabled={desk.kind === 'settling'}
                        data-testid="settle-now-credit-toggle"
                      />
                      <span>
                        Apply store credit — {formatMoney(creditWouldApplyCents)} of{' '}
                        {formatMoney(creditAvailableCents)} on the account
                        {creditWouldApplyCents >= deskAmountCents
                          ? ' (covers the whole amount)'
                          : `, leaving ${formatMoney(deskAmountCents - creditWouldApplyCents)} to collect`}
                      </span>
                    </label>
                  </div>
                ) : null}
                {/* Ted #4 (2026-09-04): the tender pick had only an aria-label,
                    so the three ghost buttons read as inert and the disabled
                    confirm (55% opacity = "light blue") looked like the option
                    itself. The required step is now VISIBLE text, and the
                    disabled confirm says what unblocks it. */}
                {!creditCoversAll ? (
                  <>
                    <p
                      id="settle-now-tender-label"
                      className="ferro-field__label ferro-settle-now__tender-label"
                    >
                      {appliedCreditCents > 0
                        ? `How was the remaining ${formatMoney(remainderCents)} paid?`
                        : 'How was it paid?'}
                    </p>
                    <div
                      className="ferro-settle-now__methods"
                      role="radiogroup"
                      aria-labelledby="settle-now-tender-label"
                    >
                      {DESK_TENDERS.map((t) => (
                        <button
                          key={t.value}
                          type="button"
                          role="radio"
                          aria-checked={deskTender === t.value}
                          className={
                            deskTender === t.value
                              ? 'ferro-btn ferro-btn--primary'
                              : 'ferro-btn ferro-btn--ghost'
                          }
                          onClick={() => {
                            setDeskTender(t.value);
                            if (t.value !== 'check') setCheckNumber(''); // Sawyer 05 end-of-day-tenders (C4)
                          }}
                          disabled={desk.kind === 'settling'}
                          data-testid={`settle-now-tender-${t.value}`}
                        >
                          {t.label}
                        </button>
                      ))}
                    </div>
                    {/* Sawyer 05 end-of-day-tenders (C4) — only while Check is the tender. */}
                    {deskTender === 'check' ? (
                      <CheckNumberField value={checkNumber} onChange={setCheckNumber} disabled={desk.kind === 'settling'} />
                    ) : null}
                    {deskTender === null ? (
                      <p
                        className="ferro-field__hint ferro-settle-now__tender-hint"
                        data-testid="settle-now-tender-hint"
                      >
                        Pick Cash, Check or Card terminal above to record this
                        payment.
                      </p>
                    ) : null}
                  </>
                ) : (
                  <p
                    className="ferro-field__hint ferro-settle-now__tender-hint"
                    data-testid="settle-now-credit-covers"
                  >
                    Store credit covers the whole amount — nothing else to
                    collect.
                  </p>
                )}
                {desk.kind === 'error' ? (
                  <div
                    className="ferro-dashboard__error"
                    role="alert"
                    data-testid="settle-now-desk-error"
                  >
                    <p>{desk.message}</p>
                  </div>
                ) : null}
                <div className="ferro-settle-now__actions">
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--primary"
                    onClick={() => void handleDeskSettle()}
                    disabled={
                      desk.kind === 'settling' ||
                      (!creditCoversAll && deskTender === null)
                    }
                    data-testid="settle-now-desk-confirm"
                  >
                    {desk.kind === 'settling'
                      ? 'Recording…'
                      : creditCoversAll
                        ? `Record ${formatMoney(deskAmountCents)} as paid with store credit`
                        : `Record ${formatMoney(deskAmountCents)} as paid`}
                  </button>
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--ghost"
                    onClick={() => setMethod('choose')}
                    disabled={desk.kind === 'settling'}
                    data-testid="settle-now-desk-back"
                  >
                    Back
                  </button>
                </div>
              </div>
            ) : null}

            <p className="ferro-muted ferro-settle-now__hint">
              Need loyalty redemption or manual card entry? Use the Payments
              console — this visit is already in its open-invoices queue.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * #1157 E2.3 — the package rows at the top of the payment choices. Renders
 * nothing when no package of this visit's service is on file (the common
 * case), a sentence when the read failed (never silence — the desk would
 * charge a card for a prepaid visit), and one button per package otherwise:
 * "Use package (33 left)", disabled with the reason when it cannot cover the
 * whole visit or has expired.
 */
function PackageChoices({
  state,
  use,
  disabled,
  collectingCart,
  onUse,
  onRetry,
}: {
  state: { kind: 'loading' } | { kind: 'ready'; options: AdminPackageCoverOptions } | { kind: 'unknown' };
  use:
    | { kind: 'idle' }
    | { kind: 'covering' }
    | { kind: 'error'; message: string }
    | { kind: 'partly'; message: string };
  disabled: boolean;
  collectingCart: boolean;
  onUse: (pkg: AdminPackageCoverPackage) => void;
  onRetry: () => void;
}): JSX.Element | null {
  const status =
    use.kind === 'error' ? (
      <div className="ferro-dashboard__error" role="alert" data-testid="settle-now-package-error">
        <p>{use.message}</p>
      </div>
    ) : use.kind === 'partly' ? (
      <p className="ferro-field__hint" role="status" data-testid="settle-now-package-partly">
        {use.message}
      </p>
    ) : null;

  if (state.kind === 'loading') return status;
  if (state.kind === 'unknown') {
    return (
      <div className="ferro-settle-now__package" data-testid="settle-now-package-unknown" role="status">
        <p className="ferro-field__hint">
          Could not check for a package on this account — look at the customer&rsquo;s profile before
          charging a card.
        </p>
        <button type="button" className="ferro-btn ferro-btn--ghost" onClick={onRetry} data-testid="settle-now-package-retry">
          Check again
        </button>
        {status}
      </div>
    );
  }
  const o = state.options;
  if (o.notApplicable !== null || o.packages.length === 0) return status;
  const dogs = o.daysNeeded === 1 ? '1 day' : `${o.daysNeeded} days — one per dog`;
  return (
    <div className="ferro-settle-now__package" data-testid="settle-now-package">
      {o.packages.map((p) => {
        const label = p.label ?? defaultPackageLabel(p);
        const why = p.expired
          ? `expired ${p.expiresOn ?? ''}`.trim()
          : !p.usable
            ? `needs ${o.daysNeeded}, ${p.remainingDays} left`
            : null;
        return (
          <button
            key={p.id}
            type="button"
            className={p.usable ? 'ferro-btn ferro-btn--primary' : 'ferro-btn ferro-btn--ghost'}
            disabled={disabled || !p.usable}
            onClick={() => onUse(p)}
            aria-describedby={`settle-now-package-detail-${p.id}`}
            data-testid={`settle-now-use-package-${p.id}`}
          >
            Use package ({p.remainingDays} left)
            <span
              id={`settle-now-package-detail-${p.id}`}
              className="ferro-settle-now__package-detail"
            >
              {label} · {why ?? `uses ${dogs}`}
              {o.amountDueAfterCents > 0 && p.usable
                ? ` · ${formatMoney(o.amountDueAfterCents)} still due`
                : ''}
              {collectingCart && p.usable ? ' · this visit' : ''}
            </span>
          </button>
        );
      })}
      {status}
    </div>
  );
}
